// Events of masters, partners, product, materials and costing [02 §5.1; 04 BR-COST-008, BR-ACT-003/004;
// 05 §17]. Payloads are camelCase JSON; every id is a kernel Id.
import { z } from 'zod';

import { businessDateSchema, idSchema } from '../kernel/index.ts';
import {
  currencyCodeSchema,
  unitRateSchema,
  FX_RATE_TYPE,
  fxRateDecimalSchema,
} from '../units/index.ts';
import { qtyDtoSchema } from '../dto/scalars.ts';
import { FABRIC_TEST_RESULT, FABRIC_TEST_STAGE } from '../enums/index.ts';
import { defineEvent } from './define.ts';

const changeKindValues = ['created', 'updated', 'deactivated', 'reactivated'] as const;

export const mastersFxRateSetV1 = defineEvent({
  type: 'masters.fx_rate.set',
  version: 1,
  aggregateType: 'fx_rate',
  specRefs: ['02 §5.1'],
  payload: z.object({
    fxRateId: idSchema,
    currency: currencyCodeSchema,
    rateType: FX_RATE_TYPE.schema,
    effectiveFrom: businessDateSchema,
    rateToBase: fxRateDecimalSchema,
  }),
});

export const mastersSettingChangedV1 = defineEvent({
  type: 'masters.setting.changed',
  version: 1,
  aggregateType: 'setting_value',
  specRefs: ['02 §5.1'],
  payload: z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/),
    scope: z.string().min(1),
    scopeId: idSchema.nullable(),
    effectiveFrom: businessDateSchema,
    value: z.json(),
  }),
});

const partnerChangedPayload = z.object({
  partnerId: idSchema,
  code: z.string().min(1),
  change: z.enum(changeKindValues),
  fields: z.array(z.string().min(1)),
});

// `partners.*.changed` [02 §5.1] expanded to the partner kinds the context owns [assumption].
export const partnersCustomerChangedV1 = defineEvent({
  type: 'partners.customer.changed',
  version: 1,
  aggregateType: 'partner',
  specRefs: ['02 §5.1 partners.*.changed'],
  payload: partnerChangedPayload,
});

export const partnersSupplierChangedV1 = defineEvent({
  type: 'partners.supplier.changed',
  version: 1,
  aggregateType: 'partner',
  specRefs: ['02 §5.1 partners.*.changed'],
  payload: partnerChangedPayload,
});

export const partnersContractorChangedV1 = defineEvent({
  type: 'partners.contractor.changed',
  version: 1,
  aggregateType: 'partner',
  specRefs: ['02 §5.1 partners.*.changed'],
  payload: partnerChangedPayload,
});

export const partnersCarrierChangedV1 = defineEvent({
  type: 'partners.carrier.changed',
  version: 1,
  aggregateType: 'partner',
  specRefs: ['02 §5.1 partners.*.changed'],
  payload: partnerChangedPayload,
});

export const productStyleChangedV1 = defineEvent({
  type: 'product.style.changed',
  version: 1,
  aggregateType: 'style',
  specRefs: ['02 §5.1'],
  payload: z.object({
    styleId: idSchema,
    styleMasterCode: z.string().length(17),
    change: z.enum([...changeKindValues, 'lifecycle_changed']),
    lifecycleStatus: z.string().min(1).nullable(),
    fields: z.array(z.string().min(1)),
  }),
});

export const productSpecApprovedV1 = defineEvent({
  type: 'product.spec.approved',
  version: 1,
  aggregateType: 'style_spec',
  specRefs: ['02 §5.1'],
  payload: z.object({
    specId: idSchema,
    styleId: idSchema,
    revision: z.int().positive(),
    approvedBy: idSchema,
    approvedOn: businessDateSchema,
  }),
});

export const materialsLotCreatedV1 = defineEvent({
  type: 'materials.lot.created',
  version: 1,
  aggregateType: 'lot',
  specRefs: ['05 §17'],
  payload: z.object({
    lotId: idSchema,
    materialColourId: idSchema,
    dyeLot: z.string().min(1),
    goodsReceiptId: idSchema.nullable(),
    qty: qtyDtoSchema,
    unitCost: unitRateSchema,
  }),
});

export const materialsHoldPlacedV1 = defineEvent({
  type: 'materials.hold.placed',
  version: 1,
  aggregateType: 'quality_hold',
  specRefs: ['05 §17 materials.hold.placed', '02 §5.1 materials.hold.set'],
  /** CR-W2a-39 R-CORE-4: `lotId` null for item × warehouse holds. */
  payload: z.object({
    holdId: idSchema,
    lotId: idSchema.nullable(),
    rollId: idSchema.nullable(),
    reasonCode: z.string().min(1),
    fabricTestId: idSchema.nullable(),
  }),
});

export const materialsHoldReleasedV1 = defineEvent({
  type: 'materials.hold.released',
  version: 1,
  aggregateType: 'quality_hold',
  specRefs: ['05 §17', '02 §5.1'],
  payload: z.object({
    holdId: idSchema,
    lotId: idSchema,
    rollId: idSchema.nullable(),
    releasedBy: idSchema,
    note: z.string().nullable(),
  }),
});

export const materialsTestResultRecordedV1 = defineEvent({
  type: 'materials.test.result_recorded',
  version: 1,
  aggregateType: 'fabric_test',
  specRefs: ['05 §17'],
  /** CR-W2a-39 R-CORE-4: `lotId` null for development / customer tests without a lot. */
  payload: z.object({
    fabricTestId: idSchema,
    lotId: idSchema.nullable(),
    stage: FABRIC_TEST_STAGE.schema,
    result: FABRIC_TEST_RESULT.schema,
    recordedOn: businessDateSchema,
  }),
});

export const costingSheetApprovedV1 = defineEvent({
  type: 'costing.sheet.approved',
  version: 1,
  aggregateType: 'cost_sheet',
  specRefs: ['02 §5.1 costing.sheet.approved', '04 BR-COST-008 costing.version_approved'],
  payload: z.object({
    costSheetId: idSchema,
    versionId: idSchema,
    styleId: idSchema,
    approvedOn: businessDateSchema,
    totalCost: unitRateSchema,
    marketPrices: z.array(z.object({ market: z.string().min(1), approvedPrice: unitRateSchema })),
  }),
});

/** BR-ACT-004 {style, unit cost, variance}; re-closing after reopen emits only the delta (BR-ACT-003). */
export const costingActualClosedV1 = defineEvent({
  type: 'costing.actual.closed',
  version: 1,
  aggregateType: 'actual_cost',
  specRefs: ['04 BR-ACT-004 costing.actual_closed', '07 §10.2 P13'],
  payload: z.object({
    actualCostId: idSchema,
    styleId: idSchema,
    closedOn: businessDateSchema,
    unitCost: unitRateSchema,
    variance: unitRateSchema,
    isDelta: z.boolean(),
  }),
});

export const PLM_EVENTS = [
  mastersFxRateSetV1,
  mastersSettingChangedV1,
  partnersCustomerChangedV1,
  partnersSupplierChangedV1,
  partnersContractorChangedV1,
  partnersCarrierChangedV1,
  productStyleChangedV1,
  productSpecApprovedV1,
  materialsLotCreatedV1,
  materialsHoldPlacedV1,
  materialsHoldReleasedV1,
  materialsTestResultRecordedV1,
  costingSheetApprovedV1,
  costingActualClosedV1,
] as const;
